import { Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseComponent } from '../../../../shared/components/component-base';
import { Breadcrumb } from '../../../../shared/models/breadcrumb';
import { ExportType } from '../../../../shared/models/search-base';
import { SelectList } from '../../../../shared/models/select-list';
import { ProcessEnum } from '../../../../shared/models/process-enum.model';
import { FileHelper } from '../../../../shared/services/file-helper';
import { RequestListDto, SearchRequestVM } from '../../../workFlow/model/search-request-dto';
import { WorkflowService } from '../../../workFlow/services/workflow-service';
import { GeneralRequestService } from '../../../common/services/general-request.service';

@Component({
  selector: 'app-approvals-history',
  templateUrl: './approvals-history.component.html',
})
export class ApprovalsHistoryComponent extends BaseComponent {

  searchParam: SearchRequestVM = new SearchRequestVM();
  data!: RequestListDto[];
  isSubmitted: boolean = false;
  statusList: SelectList[] = [];
  requestTypeList: SelectList[] = [];
  exportFileName: string = "approvals-history";
  public searchForm: FormGroup;

  constructor(
    public _workflowService?: WorkflowService,
    private fileHelper?: FileHelper,
    private generalRequestService?: GeneralRequestService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.initBreadcrumb();
    // Filter for General Approval requests only
    this.searchParam.requestTypeId = ProcessEnum.GeneralApproval.toString();
    this.getStatusList(ProcessEnum.GeneralApproval);
    this.loadData();
  }

  initForm() {
    this.searchForm = new FormGroup({
      requestNumber: new FormControl(),
      requestStatusId: new FormControl(),
      dateFrom: new FormControl(),
      dateTo: new FormControl(),
    });
  }

  initBreadcrumb() {
    this.pageTitle = this.translate.instant("approvalsHistory.title");
    this.breadcrumbItems = [
      { label: this.translate.instant("approvalsHistory.title"), isActive: true }
    ];
  }

  loadData() {
    // Ensure General Approval filter is applied
    this.searchParam.requestTypeId = ProcessEnum.GeneralApproval.toString();

    this._workflowService.getRequestsList(this.searchParam).subscribe({
      next: (res) => {
        this.data = this.formatRequestDates(res.value.items);
        this.searchParam = res.value;
        // Re-apply General Approval filter
        this.searchParam.requestTypeId = ProcessEnum.GeneralApproval.toString();
      },
      error: (error: Error) => {
        console.log(error.message);
      },
    });
  }

  formatRequestDates(items: RequestListDto[]): RequestListDto[] {
    return items.map(item => {
      // Format date
      if (item.requestDate) {
        const date = new Date(item.requestDate);
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const isAm = hours < 12;

        if (this.isArabic) {
          const arabicAmPm = isAm ? 'ص' : 'م';
          const hours12 = hours % 12 || 12;
          item.requestDateDisplay = `${day}/${month}/${year} ${hours12}:${minutes} ${arabicAmPm}`;
        } else {
          const amPm = hours < 12 ? 'AM' : 'PM';
          const hours12 = hours % 12 || 12;
          item.requestDateDisplay = `${day}/${month}/${year} ${hours12}:${minutes} ${amPm}`;
        }
      }

      // Combine status name and activity name only for Waiting status
      if (item.requestStatusId === 5) {
        if (this.isArabic) {
          item.displayStatus = `${item.requestStatusNameAr} - ${item.processActivityNameAr || ''}`;
        } else {
          item.displayStatus = `${item.requestStatusNameEn} - ${item.processActivityNameEn || ''}`;
        }
      } else {
        if (this.isArabic) {
          item.displayStatus = item.requestStatusNameAr;
        } else {
          item.displayStatus = item.requestStatusNameEn;
        }
      }

      return item;
    });
  }

  getPagedData(data) {
    this.searchParam.pageNumber = data;
    this.loadData();
  }

  searchData(isExport?: boolean, exportType?: ExportType) {
    if (isExport != null) {
      this.searchParam.isExport = isExport;
      if (exportType != null) this.searchParam.exportTypeId = exportType;
      else this.searchParam.exportTypeId = ExportType.Xlsx;
    }
    this.loadData();
  }

  onsubmit() {
    this.isSubmitted = true;
    this.searchParam.pageNumber = 1;
    this.loadData();
  }

  onReset() {
    this.searchParam.requestNumber = null;
    this.searchParam.requestStatusId = null;
    this.searchParam.dateFrom = null;
    this.searchParam.dateTo = null;
    this.searchParam.pageNumber = 1;
    // Keep General Approval filter
    this.searchParam.requestTypeId = ProcessEnum.GeneralApproval.toString();
    this.loadData();
  }

  getStatusBadgeClass(requestStatusId: number): string {
    if (requestStatusId === 2) {
      return 'bg-success-subtle text-success'; // Approved
    } else if (requestStatusId === 3) {
      return 'bg-danger-subtle text-danger'; // Rejected
    } else {
      return 'bg-primary-subtle text-primary'; // Default
    }
  }

  downloadSignatureForm(baseRequestId: string): void {
    this.generalRequestService.generateGeneralRequestSignatureForm(baseRequestId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `GeneralRequest_Signature_${baseRequestId}.docx`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading signature form:', error);
        this.toastrService.error(this.translate.instant('common.errorContent'));
      }
    });
  }

  getStatusList(processId?: number) {
    this._workflowService.getRequestStatusList(processId).subscribe({
      next: (res: any) => {
        // Filter only Approved, Rejected, and Waiting statuses
        const allowedStatusIds = [2, 3, 5]; // Approved=2, Rejected=3, Waiting=5
        this.statusList = res.value.filter((item: SelectList) => allowedStatusIds.includes(Number(item.id)));
      },
      error: (error: Error) => { },
    });
  }

  getTypesList() {
    this._workflowService.getRequestTypesList().subscribe({
      next: (res: any) => {
        this.requestTypeList = res.responseObject;
      },
      error: (error: Error) => { },
    });
  }

  onExportAsExcelClick() {
    this.isSubmitted = true;
    this.exportRequests(ExportType.Xlsx, this.exportFileName + ".xlsx");
  }

  exportRequests(exportType: ExportType, fileName: string) {
    this.searchParam.exportTypeId = exportType;
    this.searchParam.isExport = true;
    // Ensure General Approval filter is applied
    this.searchParam.requestTypeId = ProcessEnum.GeneralApproval.toString();
    this._workflowService.exportRequests(this.searchParam)
      .subscribe({
        next: (res) => {
          this.fileHelper.downloadFile(res, fileName);
        },
        error: error => this.fileHelper.handleError(error)
      });
  }
}
